import { AuthLayout } from '@/components/auth'
import { Button, ButtonLink, Icon } from '@tempots/beatui'
import { attr, html, prop, OneOfValue, on } from '@tempots/dom'
import { Anchor, NavigationService } from '@tempots/ui'
import { profile, authStore } from '@/services/gateway'

type VerifyState = 'loading' | 'success' | 'error' | 'needs-login'

export function VerifyEmailChangePage() {
  const tokenValue =
    new URLSearchParams(window.location.search).get('token') || ''
  const state = prop<VerifyState>('loading')
  const errorMessage = prop<string>('')
  const newEmail = prop<string>('')

  // Check if user is logged in before attempting verification
  const checkAndVerify = async () => {
    if (!tokenValue) {
      state.set('error')
      errorMessage.set('No verification token provided.')
      return
    }

    // Check if user is authenticated
    const user = authStore.user.get()
    if (!user) {
      // Try to refresh auth state first
      try {
        await authStore.refresh()
        const refreshedUser = authStore.user.get()
        if (!refreshedUser) {
          state.set('needs-login')
          return
        }
      } catch {
        state.set('needs-login')
        return
      }
    }

    // User is logged in, attempt verification
    try {
      const updatedProfile = await profile.verifyEmailChange({
        token: tokenValue,
      })
      newEmail.set(updatedProfile.email)
      state.set('success')
      // Refresh auth store to update user info
      await authStore.refresh()
    } catch (error: unknown) {
      state.set('error')
      errorMessage.set(
        error instanceof Error
          ? error.message
          : 'Verification failed. The link may have expired or you may be signed in with the wrong account.'
      )
    }
  }

  // Start verification on component initialization
  checkAndVerify()

  return AuthLayout(
    html.div(
      attr.class('max-w-md w-full text-center space-y-6'),
      OneOfValue(state, {
        loading: () =>
          html.div(
            attr.class('space-y-4'),
            html.div(
              attr.class(
                'w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto animate-pulse'
              ),
              Icon(
                { icon: 'mdi:email-sync' },
                attr.class('w-8 h-8 text-brand-600')
              )
            ),
            html.h2(
              attr.class('text-xl font-semibold text-gray-900'),
              'Verifying your new email...'
            ),
            html.p(
              attr.class('text-gray-600'),
              'Please wait while we verify your email change request.'
            )
          ),
        success: () =>
          html.div(
            attr.class('space-y-6'),
            html.div(
              attr.class(
                'w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto'
              ),
              Icon({ icon: 'mdi:check' }, attr.class('w-8 h-8 text-green-600'))
            ),
            html.div(
              attr.class('space-y-2'),
              html.h2(
                attr.class('text-xl font-semibold text-gray-900'),
                'Email Changed Successfully!'
              ),
              html.p(
                attr.class('text-gray-600'),
                html.span('Your email has been changed to '),
                html.strong(newEmail),
                html.span('. You can now use your new email to sign in.')
              )
            ),
            ButtonLink(
              { href: '/profile', fullWidth: true },
              attr.class('block'),
              html.span(
                attr.class('flex items-center justify-center gap-2'),
                'Go to Profile',
                Icon({ icon: 'mdi:chevron-right' }, attr.class('w-4 h-4'))
              )
            )
          ),
        error: () =>
          html.div(
            attr.class('space-y-6'),
            html.div(
              attr.class(
                'w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto'
              ),
              Icon(
                { icon: 'mdi:alert-circle' },
                attr.class('w-8 h-8 text-red-600')
              )
            ),
            html.div(
              attr.class('space-y-2'),
              html.h2(
                attr.class('text-xl font-semibold text-gray-900'),
                'Verification Failed'
              ),
              html.p(
                attr.class('text-gray-600'),
                errorMessage.map(
                  (msg: string) =>
                    msg || 'The verification link is invalid or has expired.'
                )
              )
            ),
            Anchor(
              { href: '/profile' },
              attr.class('block'),
              Button(
                { fullWidth: true },
                html.span(
                  attr.class('flex items-center justify-center gap-2'),
                  Icon({ icon: 'mdi:arrow-left' }, attr.class('w-4 h-4')),
                  'Back to Profile'
                )
              )
            )
          ),
        'needs-login': () =>
          html.div(
            attr.class('space-y-6'),
            html.div(
              attr.class(
                'w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto'
              ),
              Icon(
                { icon: 'mdi:account-alert' },
                attr.class('w-8 h-8 text-yellow-600')
              )
            ),
            html.div(
              attr.class('space-y-2'),
              html.h2(
                attr.class('text-xl font-semibold text-gray-900'),
                'Sign In Required'
              ),
              html.p(
                attr.class('text-gray-600'),
                'Please sign in to your account to verify your email change.'
              )
            ),
            html.button(
              attr.class(
                'w-full py-3 px-4 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2'
              ),
              attr.type('button'),
              on.click(() => {
                // Store the current URL to redirect back after login
                const returnUrl =
                  window.location.pathname + window.location.search
                NavigationService.navigate(
                  `/signin?returnUrl=${encodeURIComponent(returnUrl)}`
                )
              }),
              Icon({ icon: 'mdi:login' }, attr.class('w-4 h-4')),
              'Sign In'
            )
          ),
      })
    )
  )
}
